import ApiCore from "./api_core"
import { IdentificationTypeName, NewPatient, NewPersonAttribute, NewState, PersonAttribute } from "./types"

export default class {
    readonly PHONE_NUMBER_ATTRIBUTE = 12 
    patientID: number = 0
    gender: string = ''
    currentTA: string = ''
    currentVillage: string = ''
    currentDistrict: string = ''
    homeVillage: string = ''
    homeDistrict: string = ''
    homeTA: string = ''
    phone: string = ''
    birthdate: string = ''
    firstName: string = ''
    middleName: string = ''
    lastName: string =''
    identifiers: any = []

    constructor(patientID: number=0) {
        this.patientID = patientID
    }

    async load() {
        const patientRequest = await this.get(this.patientID)
        if (patientRequest.ok) this.set(patientRequest.data)
        return patientRequest
    }

    set(patient: any) {
        const names = patient?.person?.names[0]
        const addresses = patient.person?.addresses[0] || {};
        this.firstName = names?.given_name
        this.middleName = names?.middle
        this.lastName = names?.family_name
        this.birthdate = patient?.person?.birthdate
        this.gender = patient?.person?.gender
        this.currentTA = addresses?.township_division
        this.currentVillage = addresses?.city_village
        this.currentDistrict = addresses?.state_province
        this.homeVillage = addresses?.ancestryVillage
        this.homeTA = addresses?.ancestryTA
        this.homeDistrict = addresses?.ancestryDistrict
        this.identifiers = patient?.patient_identifiers||[]
        this.phone = this.getPersonAttribute(
            this.PHONE_NUMBER_ATTRIBUTE, patient?.person?.person_attributes
        )
    }

    get(id: number=this.patientID) { return ApiCore.getJson(`/patients/${id}`) }

    isMale() { return ['Male', 'M'].includes(this.gender) }

    isFemale() { return ['Female', 'F'].includes(this.gender) }

    getAge() {
        const currentDate = new Date();
        const birthDate = new Date(this.birthdate);

        let age = currentDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = currentDate.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) age--;
        return age;
    }

    getFullName() { return `${this.firstName} ${this.middleName||''} ${this.lastName}` }

    getDocID() { this.findIdentifierByType('DDE person document ID') }

    getNpid() { return this.findIdentifierByType('National id') }

    getMwNationalID() { return this.findIdentifierByType('Malawi National ID') }

    hasIdentifierType(type: IdentificationTypeName) {
        return this.identifiers.find({ type: { name : type } }) ? true : false
    }

    createIdentifier(identifier: string, type: IdentificationTypeName) {
        return ApiCore.postJson('patient_identifiers', {
            'identifier': identifier,
            'identifier_type': type,
            'patient_id': this.patientID
        })
    }

    visits(programID: number) {
        return ApiCore.getJson(`patients/${this.patientID}/visits`, { 'program_id': programID })
    }

    void() { return ApiCore.void(`patients/${this.patientID}`) }

    isChildBearing() {
        const age = this.getAge()
        return this.isFemale() && age >= 12 && age <= 50
    }

    private findIdentifierByType(type: IdentificationTypeName) {
        return this.identifiers
            .filter((i: any) => i.type.name === type)
            .sort((a: any, b: any) => a['date_created'] < b['date_created'] ? 1 : 0)
            .reduce((defaultID: any, curID: any) => defaultID === 'Unknown' ? curID.identifier : defaultID, 'Unknown')
    }

    getRelationships() { return ApiCore.getJson(`people/${this.patientID}/relationships`); }

    createRelation(patientB: number, relationTypeID: number) {
        return ApiCore.postJson(`people/${this.patientID}/relationships`, {
            'relationship_type_id': relationTypeID,
            'relation_id': patientB
        })
    }

    voidRelation(relationID: number) {
		return ApiCore.void(`people/${this.patientID}/relationships/${relationID}`)
    }

    getPersonAttribute(personAttributeTypeID: number, personAttributes: PersonAttribute[]) {
        const attributes = personAttributes.filter(attr => {
            return attr.person_attribute_type_id == personAttributeTypeID;
        });
        return attributes.length > 0 ? attributes[0].value : "";
    }

    createPatient(patientID: number, programID: number) {
        return ApiCore.postJson('patients', { program_id: programID, person_id: patientID })
    }

    updatePatient(person: NewPatient, programID: number) {
        return ApiCore.putJson(`people/${this.patientID}`, { ...person, program_id: programID })
    }
    
    enrollInProgram(programID: number, date: string) {
        return ApiCore.postJson(`patients/${this.patientID}/programs`, {
            program_id: programID, date_enrolled: date
        })
    }
    
    createProgramState(programID: number, state: NewState) {
        return ApiCore.postJson(`programs/${programID}/patients/${this.patientID}/states`, state) 
    }

    voidProgram(patientProgramID: number) {
        return ApiCore.void(`patient_programs/${patientProgramID}`)
    }

    voidState(programID: number, stateID: number) {
        return ApiCore.void(`programs/${programID}/patients/${this.patientID}/states/${stateID}`)
    }

    getPatientStates(programID: number) {
        return ApiCore.getJson(`programs/${programID}/patients/${this.patientID}/states`)
    }

    getPatientPrograms() {
        return ApiCore.getJson(`patients/${this.patientID}/programs`)
    }

    getProgramInformation(programID: number) {
        return ApiCore.getJson(`programs/${programID}/patients/${this.patientID}`);
    }

    getLabOrders(params={}) {
        return ApiCore.getJson('lab/orders', { patient_id: this.patientID, ...params });
    }

    async createAttributes(attributes: NewPersonAttribute[]) {
        let status = []
        for(const index in attributes) {
            const res = await ApiCore.postJson('person_attributes', {
                ...attributes[index], person_id: this.patientID
            })
            status.push(res.ok)
        }
        return status.every(Boolean)
    }

    async registerPatient(person: NewPatient, programID: number) {
        const personRequest = await ApiCore.postJson('people', person)
        if (!personRequest.ok) return personRequest
        this.patientID = personRequest.data.person_id
        const newPatientRequest = await this.createPatient(this.patientID, programID)
        if (!newPatientRequest.ok) return newPatientRequest
        this.set(newPatientRequest.data)
        return newPatientRequest
    }

    getDrugOrders(programID: number, date: string) {
        return ApiCore.getJson('drug_orders', {
            program_id: programID,
            patient_id: this.patientID,
            date: date
        })
    }

    getDrugDosages(programID: number, drugID: number, date: string) {
        return ApiCore.getJson(`programs/${programID}/patients/${this.patientID}/drug_doses`, {
            drug_id: drugID, date})
    }

    getLastDrugsReceived(programID: number, date: number) {
        return ApiCore.getJson(`patients/${this.patientID}/last_drugs_received`, {
            program_id: programID,
            date: date
        })
    }

    getAllDrugOrders(programID: number, pageSize=1000) {
        return ApiCore.getJson('drug_orders', {
            patient_id: this.patientID,
            program_id: programID,
            page_size: pageSize
        })
    }
}
